import { ChangeDetectionStrategy, Component, type OnInit, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { TenantService } from '@/cloud/services/tenant.service';
import { BreadcrumbsComponent, type BreadcrumbItem } from '@/shared/breadcrumbs/breadcrumbs.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { UserAvatarComponent } from '@/shared/user-avatar/user-avatar.component';
import { RoleBadgeComponent } from '@/shared/role-badge/role-badge.component';
import { ServiceDeskGrantComponent } from '@/cloud/components/assistant/service-desk-grant.component';

/**
 * User management: invite-first user creation, per-namespace role chips, and a
 * detail drawer for profile/roles/account actions. Manual create-with-password
 * remains as a secondary action for environments where email links aren't practical.
 */
@Component({
    selector: 'app-users',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        DialogModule,
        DrawerModule,
        InputTextModule,
        SelectModule,
        TableModule,
        TagModule,
        TooltipModule,
        ConfirmDialogModule,
        BreadcrumbsComponent,
        EmptyStateComponent,
        UserAvatarComponent,
        RoleBadgeComponent,
        ServiceDeskGrantComponent
    ],
    providers: [ConfirmationService],
    templateUrl: './users.html',
    styleUrl: './users.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class Users implements OnInit {
    api = inject(CloudApiService);
    private confirmationService = inject(ConfirmationService);
    private messageService = inject(MessageService);

    readonly breadcrumbs: BreadcrumbItem[] = [
        { label: 'Dashboard', routerLink: '/dashboard' },
        { label: 'Users', routerLink: '/admin/users' }
    ];

    users = signal<any[]>([]);
    loading = signal(false);
    searchQuery = signal('');
    selectedNamespace = signal<string>('');

    // Invite dialog (primary)
    showInvite = signal(false);
    inviting = signal(false);
    inviteEmail = signal('');
    inviteNamespaceId = signal<string>('');
    inviteRoleId = signal('reader');

    // Manual create dialog (secondary, superadmin escape hatch)
    showCreate = signal(false);
    creating = signal(false);
    createForm = signal({ username: '', password: '', displayName: '', email: '' });

    // Detail drawer
    drawerVisible = signal(false);
    selectedUser = signal<any | null>(null);
    savingRole = signal<string | null>(null); // namespaceId currently saving
    addGrantNamespaceId = signal<string>('');
    addGrantRoleId = signal('reader');

    isSuperAdmin = computed(() => !!this.api.currentUser()?.superAdmin);
    roleOptions = computed(() => (this.api.roles() ?? []).map((r) => ({ id: r.id, name: r.name })));
    namespaceOptions = computed(() => (this.api.namespaces() ?? []).map((n) => ({ id: n.id ?? n.Id, name: n.name ?? n.Name })));
    namespaceFilterOptions = computed(() => [{ id: '', name: 'All tenants' }, ...this.namespaceOptions()]);
    private tenants = inject(TenantService);
    /** Default the list to the tenant chosen in the topbar; "All tenants" stays available for cross-tenant admin. */
    private followTenant = effect(() => {
        const id = this.tenants.selectedTenantId();
        if (id) this.selectedNamespace.set(id);
    });

    filteredUsers = computed(() => {
        let list = this.users();
        const ns = this.selectedNamespace();
        if (ns) {
            list = list.filter((u) => u?.superAdmin || (u.permissions || []).some((p: any) => this.permNs(p) === ns));
        }
        const query = this.searchQuery().toLowerCase().trim();
        if (query) {
            list = list.filter((u) => (u.username || '').toLowerCase().includes(query) || (u.displayName || '').toLowerCase().includes(query) || (u.email || '').toLowerCase().includes(query));
        }
        return list;
    });

    /** The toolbar's "n / m shown" count: dashes until the first load returns. */
    resultCount = computed(() => (this.loading() && !this.users().length ? '— / —' : `${this.filteredUsers().length} / ${this.users().length}`));

    // Namespaces the drawer's user does NOT already belong to (for the add-access row)
    grantableNamespaces = computed(() => {
        const user = this.selectedUser();
        if (!user) return [];
        const memberOf = new Set((user.permissions || []).map((p: any) => this.permNs(p)));
        return this.namespaceOptions().filter((n) => !memberOf.has(n.id) && (this.isSuperAdmin() || this.api.hasCapability(n.id, 'members.manage')));
    });

    // Tenants the drawer's user belongs to: service desk access only reaches these
    selectedMemberOf = computed(() => (this.selectedUser()?.permissions || []).map((p: any) => this.permNs(p)).filter(Boolean));

    ngOnInit(): void {
        this.load();
        this.api.getNamespaces().catch(() => undefined);
        this.api.getRoles().catch(() => undefined);
    }

    load() {
        this.loading.set(true);
        this.api
            .getUsers()
            .then((users) => {
                this.users.set(users ?? []);
                // Keep the drawer in sync after mutations
                const selected = this.selectedUser();
                if (selected) {
                    this.selectedUser.set((users ?? []).find((u) => u.id === selected.id) ?? null);
                }
            })
            .catch((err) => this.toastError('Failed to Load Users', err))
            .finally(() => this.loading.set(false));
    }

    // --- Table helpers ---

    permNs(p: any): string {
        return p?.appNamespaceId ?? p?.AppNamespaceId ?? '';
    }

    permRoleId(p: any): string {
        const roleId = p?.roleId ?? p?.RoleId ?? p?.effectiveRoleId ?? p?.EffectiveRoleId;
        if (roleId) return roleId;
        const tier = p?.role ?? p?.Role ?? 0;
        return ['reader', 'writer', 'admin'][tier] ?? 'reader';
    }

    permRoleName(p: any): string {
        const roleId = this.permRoleId(p);
        return (this.api.roles() ?? []).find((r) => r.id.toLowerCase() === roleId.toLowerCase())?.name ?? roleId;
    }

    namespaceName(nsId: string): string {
        return this.namespaceOptions().find((n) => n.id === nsId)?.name ?? nsId;
    }

    isPending(user: any): boolean {
        // Placeholder invite users are disabled until first sign-in and keep the invite email as username
        return !!user?.disabled && user?.username === user?.email && !user?.superAdmin;
    }

    openDrawer(user: any) {
        this.selectedUser.set(user);
        this.addGrantNamespaceId.set('');
        this.addGrantRoleId.set('reader');
        this.drawerVisible.set(true);
    }

    // --- Invite (primary CTA) ---

    openInvite() {
        this.inviteEmail.set('');
        this.inviteNamespaceId.set('');
        this.inviteRoleId.set('reader');
        this.showInvite.set(true);
    }

    submitInvite() {
        const email = this.inviteEmail().trim();
        if (!email) return;
        const nsId = this.inviteNamespaceId();
        const permissions = nsId ? [{ appNamespaceId: nsId, role: this.legacyTier(this.inviteRoleId()), roleId: this.inviteRoleId() }] : undefined;
        this.inviting.set(true);
        this.api
            .inviteUser(email, permissions)
            .then((res) => {
                this.showInvite.set(false);
                this.messageService.add({
                    severity: res?.emailSent === false ? 'warn' : 'success',
                    summary: res?.emailSent === false ? 'Invite Created (email not sent)' : 'Invite Sent',
                    detail: email
                });
                this.load();
            })
            .catch((err) => this.toastError('Invite Failed', err))
            .finally(() => this.inviting.set(false));
    }

    // --- Manual create (secondary) ---

    openCreate() {
        this.createForm.set({ username: '', password: '', displayName: '', email: '' });
        this.showCreate.set(true);
    }

    setCreateField(field: string, value: string) {
        this.createForm.set({ ...this.createForm(), [field]: value } as any);
    }

    submitCreate() {
        const f = this.createForm();
        if (!f.username || !f.password || !f.email) return;
        this.creating.set(true);
        this.api
            .createUser(f.username, f.password, f.displayName || undefined, f.email)
            .then(() => {
                this.showCreate.set(false);
                this.messageService.add({ severity: 'success', summary: 'User Created', detail: f.username });
                this.load();
            })
            .catch((err) => this.toastError('Create Failed', err))
            .finally(() => this.creating.set(false));
    }

    // --- Drawer actions ---

    changeRole(user: any, nsId: string, roleId: string) {
        const current = (user.permissions || []).find((p: any) => this.permNs(p) === nsId);
        if (!roleId || (current && this.permRoleId(current) === roleId)) return;
        this.savingRole.set(nsId);
        this.api
            .grantPermission(user.id, nsId, this.legacyTier(roleId), roleId)
            .then(() => {
                this.messageService.add({ severity: 'success', summary: 'Role Updated', detail: `${this.namespaceName(nsId)} · ${roleId}` });
                this.load();
            })
            .catch((err) => {
                this.toastError('Role Change Failed', err);
                this.load();
            })
            .finally(() => this.savingRole.set(null));
    }

    addGrant() {
        const user = this.selectedUser();
        const nsId = this.addGrantNamespaceId();
        if (!user || !nsId) return;
        this.changeRole(user, nsId, this.addGrantRoleId());
        this.addGrantNamespaceId.set('');
    }

    revokeAccess(user: any, nsId: string) {
        this.confirmationService.confirm({
            header: 'Remove Access',
            message: `Remove ${user.displayName || user.username}'s access to ${this.namespaceName(nsId)}?`,
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger',
            acceptLabel: 'Remove',
            accept: () => {
                this.api
                    .revokePermission(nsId, user.id)
                    .then(() => {
                        this.messageService.add({ severity: 'success', summary: 'Access Removed', detail: this.namespaceName(nsId) });
                        this.load();
                    })
                    .catch((err) => this.toastError('Remove Failed', err));
            }
        });
    }

    canEditRoles(user: any): boolean {
        if (user?.superAdmin && !this.isSuperAdmin()) return false;
        return true;
    }

    isSelf(user: any): boolean {
        return this.api.currentUser()?.id === user?.id;
    }

    toggleDisabled(user: any) {
        const disable = !user.disabled;
        this.confirmationService.confirm({
            header: disable ? 'Disable Account' : 'Enable Account',
            message: `${disable ? 'Disable' : 'Enable'} ${user.displayName || user.username}? ${disable ? 'They will not be able to sign in.' : ''}`,
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: disable ? 'p-button-danger' : undefined,
            acceptLabel: disable ? 'Disable' : 'Enable',
            accept: () => {
                this.api
                    .updateUserDisabledStatus(user.id, disable)
                    .then(() => {
                        this.messageService.add({ severity: 'success', summary: disable ? 'Account Disabled' : 'Account Enabled', detail: user.username });
                        this.load();
                    })
                    .catch((err) => this.toastError('Update Failed', err));
            }
        });
    }

    toggleSuperAdmin(user: any) {
        const grant = !user.superAdmin;
        this.confirmationService.confirm({
            header: grant ? 'Grant Super Admin' : 'Remove Super Admin',
            message: grant ? `Grant ${user.displayName || user.username} super admin? They will have full access to every tenant and all administration.` : `Remove super admin from ${user.displayName || user.username}?`,
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger',
            acceptLabel: grant ? 'Grant' : 'Remove',
            accept: () => {
                this.api
                    .updateUserSuperAdminStatus(user.id, grant)
                    .then(() => {
                        this.messageService.add({ severity: 'success', summary: 'Super Admin Updated', detail: user.username });
                        this.load();
                    })
                    .catch((err) => this.toastError('Update Failed', err));
            }
        });
    }

    sendPasswordReset(user: any) {
        this.confirmationService.confirm({
            header: 'Send Password Reset',
            message: `Send a password reset email to ${user.email || user.username}?`,
            icon: 'pi pi-envelope',
            acceptLabel: 'Send',
            accept: () => {
                this.api
                    .sendPasswordReset(user.id)
                    .then((res: any) => {
                        this.messageService.add({
                            severity: res?.emailSent ? 'success' : 'warn',
                            summary: res?.emailSent ? 'Reset Email Sent' : 'Email Not Sent',
                            detail: res?.emailSent ? res.email : 'Reset link generated but the email could not be sent'
                        });
                    })
                    .catch((err) => this.toastError('Password Reset Failed', err));
            }
        });
    }

    resendInvite(user: any) {
        this.api
            .resendInvite(user.id)
            .then((res) => this.messageService.add({ severity: 'success', summary: 'Invite Sent', detail: res.email }))
            .catch((err) => this.toastError('Resend Failed', err));
    }

    deleteUser(user: any) {
        this.confirmationService.confirm({
            header: 'Delete User',
            message: `Delete ${user.displayName || user.username}? This cannot be undone.`,
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger',
            acceptLabel: 'Delete',
            accept: () => {
                this.api
                    .deleteUser(user.id)
                    .then(() => {
                        this.messageService.add({ severity: 'success', summary: 'User Deleted', detail: user.username });
                        this.drawerVisible.set(false);
                        this.load();
                    })
                    .catch((err) => this.toastError('Delete Failed', err));
            }
        });
    }

    legacyTier(roleId: string): number {
        return { reader: 0, writer: 1, admin: 2 }[roleId?.toLowerCase()] ?? 0;
    }

    private toastError(summary: string, err: any) {
        const detail = err?.error?.message ?? (typeof err?.error === 'string' ? err.error : null) ?? err?.message ?? 'An error occurred';
        this.messageService.add({ severity: 'error', summary, detail });
    }
}
